import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronRight, RefreshCw, Copy, Trash2, Filter, AlertCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useMemo, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

type SystemLogEntry = {
  timestamp: string;
  level: "error" | "warn";
  source: string;
  message: string;
  details?: any;
};

export default function AdminSystemLogs() {
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());
  const [levelFilter, setLevelFilter] = useState<"all" | "error" | "warn">("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  // Use correct tRPC pattern like other components
  const { data: rawLogs, isLoading: logsLoading, error, refetch } = trpc.system.getSystemLogs.useQuery(undefined, {
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 30000, // Cache for 30 seconds, but auto-refresh
  });

  // Debug raw data on change (one-time per query result)
  useEffect(() => {
    if (rawLogs !== undefined) {
      console.debug('[AdminSystemLogs] Raw logs data:', { type: typeof rawLogs, length: Array.isArray(rawLogs) ? rawLogs.length : 'N/A', data: rawLogs });
    }
  }, [rawLogs]);

  const clearLogsMutation = trpc.system.clearSystemLogs.useMutation({
    onSuccess: () => {
      refetch(); // Refresh the logs after clearing
    },
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch({ cancelRefetch: false });
      setLastRefreshed(new Date());
      toast.success("Logs refreshed");
    } catch (error: any) {
      toast.error("Failed to refresh logs");
      console.error("Refresh error:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Normalize logs to always be an array
  const normalizedLogs = useMemo(() => {
    if (!rawLogs) {
      return [];
    }
    if (Array.isArray(rawLogs)) {
      return rawLogs;
    }
    // Fallback for any other cases
    return [];
  }, [rawLogs]);

  // Client-side filtering and searching
  const logs = useMemo(() => {
    // Always filter on normalized logs (never null)
    return normalizedLogs.filter((log: SystemLogEntry) => {
      // Level filter
      if (levelFilter !== "all" && log.level !== levelFilter) {
        return false;
      }

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesMessage = log.message.toLowerCase().includes(searchLower);
        const matchesSource = log.source.toLowerCase().includes(searchLower);
        const matchesDetails = log.details ? JSON.stringify(log.details).toLowerCase().includes(searchLower) : false;

        if (!matchesMessage && !matchesSource && !matchesDetails) {
          return false;
        }
      }

      return true;
    });
  }, [normalizedLogs, levelFilter, searchTerm]);

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedEntries(newExpanded);
  };

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading System Logs</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Failed to load system logs. Please try again.
            </p>
            <Button
              onClick={handleRefresh}
              className="mt-4"
              variant="outline"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">System Logs</h1>
          <p className="text-muted-foreground">
            Real-time system error and diagnostic logs
            {lastRefreshed && (
              <span className="block text-xs mt-1">
                Last refreshed: {lastRefreshed.toLocaleString()}
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          disabled={logsLoading || isRefreshing}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${(logsLoading || isRefreshing) ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Toolbar */}
      {!logsLoading && normalizedLogs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Level:</span>
                <Select value={levelFilter} onValueChange={(value: "all" | "error" | "warn") => setLevelFilter(value)}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="warn">Warn</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 flex-1 min-w-64">
                <span className="text-sm font-medium">Search:</span>
                <Input
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" disabled={clearLogsMutation.isPending}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear Logs
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear All System Logs</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete all {normalizedLogs.length} system log entries. This action cannot be undone.
                      Are you sure you want to continue?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => clearLogsMutation.mutate()}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      Clear All Logs
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  // Import and call test error function
                  import("@/lib/errorReporting").then(({ reportTestError }) => {
                    reportTestError();
                    toast.info("Test error logged - check system logs in ~30 seconds");
                  });
                }}
              >
                Generate Test Error
              </Button>
            </div>

            {(levelFilter !== "all" || searchTerm) && (
              <div className="mt-3 text-sm text-muted-foreground">
                Showing {logs.length} of {normalizedLogs.length} log entries
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recent System Events</CardTitle>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="text-center py-8">
              <RefreshCw className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">Loading logs...</p>
            </div>
          ) : !logs || logs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No logs yet</h3>
              <p className="text-muted-foreground text-sm max-w-sm">
                System logs will appear here when errors or warnings occur.
                Try creating a well with invalid data to confirm logging is working.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log: SystemLogEntry, index: number) => (
                <div
                  key={`${log.timestamp}-${index}`}
                  className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <Badge
                      variant={log.level === 'error' ? 'destructive' : 'secondary'}
                      className="mt-0.5 text-xs font-medium"
                    >
                      {log.level.toUpperCase()}
                    </Badge>

                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{log.source}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          {log.details && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleExpanded(index)}
                              className="h-6 px-2 text-xs"
                            >
                              {expandedEntries.has(index) ? (
                                <ChevronDown className="h-3 w-3" />
                              ) : (
                                <ChevronRight className="h-3 w-3" />
                              )}
                              Details
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const logData = {
                                timestamp: log.timestamp,
                                level: log.level,
                                source: log.source,
                                message: log.message,
                                details: log.details
                              };
                              navigator.clipboard.writeText(JSON.stringify(logData, null, 2));
                            }}
                            className="h-6 px-2 text-xs"
                            title="Copy full log entry as JSON"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>

                      <p className="text-sm text-foreground">{log.message}</p>

                      {expandedEntries.has(index) && log.details && (
                        <div className="mt-3 p-3 bg-muted rounded-md">
                          <pre className="text-xs whitespace-pre-wrap break-words">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {!logsLoading && logs && logs.length > 0 && (
        <div className="text-xs text-muted-foreground text-center">
          Showing last {logs.length} system events (auto-refreshes every 30 seconds)
        </div>
      )}
    </div>
  );
}
